// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getBreakPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import translate from "@insite/client-framework/Translate";
import { BreakPriceDto, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";

interface PriceModel {
    price: number;
    priceDisplay: string;
}

export const getUnitNetPrice = (pricing: ProductPriceDto, qtyOrdered: number) => {
    const price = getPrice(
        pricing.unitRegularBreakPrices,
        pricing.unitNetPrice,
        pricing.unitNetPriceDisplay,
        qtyOrdered,
    );
    // XNG-Change
    if (price.price === 0) {
        price.priceDisplay = "Requires Quote";
    }
    return { price: price.price, priceDisplay: price.priceDisplay } as PriceModel;
};

export const getUnitListPrice = (pricing: ProductPriceDto, qtyOrdered: number) => {
    const price = getPrice(
        pricing.unitListBreakPrices,
        pricing.unitListPrice,
        pricing.unitListPriceDisplay,
        qtyOrdered,
    );
    // XNG-Change
    if (price.price === 0) {
        price.priceDisplay = "Requires Quote";
    }
    return { price: price.price, priceDisplay: price.priceDisplay } as PriceModel;
};

const conditionBreakPrice = (breaks: BreakPriceDto[] | null, count: number) => {
    return !breaks || breaks.length === 0 || count === 0;
};

const getPrice = (breaks: BreakPriceDto[] | null, price: number, priceToDisplay: string, qty: any) => {
    const quantity = !qty || qty === "0" ? 1 : qty;
    if (conditionBreakPrice(breaks, quantity)) {
        return { price, priceDisplay: priceToDisplay } as PriceModel;
    }

    const breakPrice = getBreakPrice(breaks, quantity);
    if (!breakPrice) {
        return { price, priceDisplay: priceToDisplay } as PriceModel;
    }

    return { price: breakPrice.breakPrice, priceDisplay: breakPrice.breakPriceDisplay } as PriceModel;
};

export const getUnitExtendedPrice = (
    productContext: ProductContextModel,
    pricing: ProductPriceDto | null | undefined,
    currencySymbol: string,
    qtyOrdered: number | null,
) => {
    if (!productContext || !productContext.product || !pricing) {
        return;
    }

    if (pricing.requiresRealTimePrice) {
        return {
            price: 0,
            priceDisplay: currencySymbol || "",
        } as PriceModel;
    }

    if (productContext.product.configurationType !== ConfigurationType.None) {
        const price = getPrice(null, pricing.unitNetPrice, pricing.unitNetPriceDisplay, qtyOrdered);
        return { price: price.price, priceDisplay: price.priceDisplay } as PriceModel;
    }

    const price = getPrice(
        pricing.unitListBreakPrices,
        pricing.extendedUnitListPrice,
        pricing.extendedUnitListPriceDisplay,
        qtyOrdered,
    );
    if (price.price === 0) {
        price.priceDisplay = translate("Requires Quote");
    }
    return { price: price.price, priceDisplay: price.priceDisplay } as PriceModel;
};
